import type { DecisionKind, IncidentSource, IncidentStatus } from './api';

/** Цвет таблетки состояния — классы из `support.css`. */
export type Ton = 'warn' | 'info' | 'ok' | 'bad' | 'gray' | 'violet';

interface Slovo { text: string; ton: Ton }

/** Состояние обращения словами для его автора-сотрудника. */
export function statusDlyaAvtora(status: IncidentStatus, confirmed: boolean): Slovo {
  switch (status) {
    case 'new':
    case 'triage': return { text: 'Разбирается', ton: 'info' };
    case 'in_work': return { text: 'Исправляется', ton: 'info' };
    case 'needs_reply': return { text: 'Нужен ваш ответ', ton: 'warn' };
    case 'needs_decision': return { text: 'Ждёт решения руководителя', ton: 'gray' };
    case 'verifying': return { text: 'Проверяется', ton: 'violet' };
    case 'released':
    case 'fixed': return confirmed ? { text: 'Исправлено', ton: 'ok' } : { text: 'Исправлено — проверьте', ton: 'warn' };
    case 'confirmed': return { text: 'Исправлено', ton: 'ok' };
    case 'obstacle': return { text: 'Передано руководителю', ton: 'gray' };
    case 'declined': return { text: 'Отклонено', ton: 'gray' };
    case 'closed': return { text: 'Закрыто', ton: 'gray' };
  }
}

/**
 * Состояние обращения словами для владельца. `author` — у кого спрошено уточнение.
 *
 * Исправленное и проверенное — «Исправлено», подтвердил автор или нет (`s774`, слова владельца
 * 11.10.2026: «давно решены, висят в процессе решения»): подтверждение — добавка, а не ожидание.
 */
export function statusDlyaVladeltsa(status: IncidentStatus, confirmed: boolean, author?: string | null): Slovo {
  switch (status) {
    case 'new': return { text: 'Принято', ton: 'info' };
    case 'triage': return { text: 'Разбирается', ton: 'info' };
    case 'in_work': return { text: 'Исправляется', ton: 'info' };
    case 'needs_reply': return { text: author ? `Ждёт ответа: ${author}` : 'Ждёт ответа сотрудника', ton: 'warn' };
    case 'needs_decision': return { text: 'Ждёт вашего решения', ton: 'warn' };
    case 'verifying': return { text: 'Выпущено, наблюдаем', ton: 'violet' };
    case 'released': return { text: 'Выпущено, идёт проверка', ton: 'violet' };
    case 'fixed': return { text: confirmed ? 'Исправлено и подтверждено' : 'Исправлено', ton: 'ok' };
    case 'confirmed': return { text: 'Исправлено и подтверждено', ton: 'ok' };
    case 'obstacle': return { text: 'Сам не справился — нужен человек', ton: 'bad' };
    case 'declined': return { text: 'Отклонено', ton: 'gray' };
    case 'closed': return { text: 'Закрыто', ton: 'gray' };
  }
}

/** Все состояния в порядке пути обращения — для фильтра «Состояние». */
export const SOSTOYANIYA: Record<'decision' | 'work' | 'proposals' | 'done', IncidentStatus[]> = {
  decision: ['needs_decision'],
  work: ['new', 'triage', 'in_work', 'needs_reply', 'verifying', 'obstacle'],
  proposals: ['new', 'triage', 'needs_decision', 'in_work', 'needs_reply', 'verifying', 'obstacle'],
  done: ['released', 'fixed', 'confirmed', 'closed', 'declined'],
};

export function istochnik(source: IncidentSource, author: string | null): string {
  if (source === 'auto') return 'Система заметила сама';
  const otkuda = source === 'ui' ? 'Из панели' : 'Из Telegram';
  return author ? `${otkuda} · ${author}` : otkuda;
}

export const ISTOCHNIKI: { value: IncidentSource; label: string }[] = [
  { value: 'auto', label: 'система сама' },
  { value: 'ui', label: 'из панели' },
  { value: 'telegram', label: 'из Telegram' },
];

/** Как решение называется на кнопках и в подписи. */
export function slovaResheniya(kind: DecisionKind): { da: string; net: string; vopros: string } {
  switch (kind) {
    case 'release': return { da: 'Разрешить выпуск', net: 'Отклонить', vopros: 'Выпустить исправление?' };
    case 'proposal': return { da: 'Взять в работу', net: 'Отклонить', vopros: 'Делать это пожелание?' };
    case 'outside_answer': return { da: 'Разрешить ответ', net: 'Отклонить', vopros: 'Запрос за рамками неисправности. Разрешить Сене ответить?' };
    case 'outside_action': return { da: 'Разрешить', net: 'Отклонить', vopros: 'Действие за рамками неисправности. Разрешить?' };
  }
}

export function ispolnitel(id: string | null | undefined): string | null {
  if (!id || id === 'pending') return null;
  if (id === 'claude_code') return 'Claude по подписке';
  if (id === 'codex_cli') return 'Codex по подписке';
  if (id === 'anthropic_api') return 'Anthropic API (платный)';
  if (id === 'openai_api') return 'OpenAI API (платный)';
  if (id.startsWith('session_')) return `Рабочая сессия ${id.slice(8)}`;
  return id;
}

const MSK = 'Europe/Moscow';
const denFmt = new Intl.DateTimeFormat('ru-RU', { timeZone: MSK, day: '2-digit', month: '2-digit', year: 'numeric' });
const vremyaFmt = new Intl.DateTimeFormat('ru-RU', { timeZone: MSK, hour: '2-digit', minute: '2-digit' });

/** Время по Москве: «сегодня, 14:32», «вчера, 18:40», «08.10, 09:15». */
export function kogda(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const den = denFmt.format(date);
  const segodnya = denFmt.format(new Date());
  const vchera = denFmt.format(new Date(Date.now() - 86_400_000));
  const chasy = vremyaFmt.format(date);
  if (den === segodnya) return `сегодня, ${chasy}`;
  if (den === vchera) return `вчера, ${chasy}`;
  return `${den.slice(0, 5)}, ${chasy}`;
}

export function chasy(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : vremyaFmt.format(date);
}

/** Сколько ждёт: «ждёт 40 мин», «ждёт 2 ч», «ждёт 3 дн». */
export function zhdyot(iso: string | null | undefined): string {
  if (!iso) return '';
  const minut = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minut < 1) return 'ждёт меньше минуты';
  if (minut < 60) return `ждёт ${minut} мин`;
  if (minut < 60 * 48) return `ждёт ${Math.round(minut / 60)} ч`;
  return `ждёт ${Math.round(minut / 1440)} дн`;
}

/**
 * Суть обращения: короткий заголовок и текст под ним.
 *
 * У сбоя, замеченного системой, заголовок служебный («QueryException: …») — человеку
 * показываем разбор простыми словами (`human`). Заголовок — первое предложение, текст —
 * всё целиком; сервер хранит их с пределом длины, поэтому оборванное помечаем «…».
 */
export function sut(title: string, summary: string | null, human?: string | null): { zagolovok: string; tekst: string | null } {
  const osnova = (human && human.trim() !== '' ? human : title).trim();
  const zagolovok = pervoePredlozhenie(osnova);
  const polnyy = (human && human.trim() !== '' ? human : summary ?? '').trim();
  const bezTochki = (t: string) => t.replace(/[.…]+$/, '');
  if (polnyy === '' || bezTochki(polnyy) === bezTochki(zagolovok)) return { zagolovok, tekst: null };
  return { zagolovok, tekst: /[.!?…»)]$/.test(polnyy) ? polnyy : `${polnyy}…` };
}

function pervoePredlozhenie(text: string): string {
  const pervoe = text.split(/(?<=[.!?])\s+/)[0].replace(/[.]$/, '');
  return pervoe.length > 140 ? `${pervoe.slice(0, 137).replace(/\s+\S*$/, '')}…` : pervoe;
}

/** Текст результата хранится продолжением фразы «Исправлено: …» — отдельно он начинается с заглавной. */
export function sZaglavnoy(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Участок хранится с пределом длины: оборванное название помечаем «…». */
export function uchastok(area: string | null): string | null {
  if (!area) return null;
  return area.length >= 60 ? `${area.replace(/\s+\S*$/, '')}…` : area;
}
